import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_DELAY_MS = 450;
// finger movement allowed before the press counts as a scroll instead
const MOVE_TOLERANCE_PX = 8;

/**
 * Long-press for touch input. Returns props for the pressed element.
 *
 * - Mouse input is ignored (desktop keeps right click and modifier keys).
 * - Moving the finger more than a few pixels cancels, so scrolling never
 *   triggers it.
 * - The click that follows a long-press is swallowed, so the row doesn't
 *   also open.
 * - The system context menu / iOS callout is suppressed on the element.
 */
export function useLongPress(onLongPress, { delay = DEFAULT_DELAY_MS, enabled = true } = {}) {
  const timerRef = useRef(null);
  const startRef = useRef(null);
  const firedRef = useRef(false);
  const callbackRef = useRef(onLongPress);
  callbackRef.current = onLongPress;

  const cancel = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    startRef.current = null;
    // the click (if any) follows right after pointerup; don't swallow a later one
    if (firedRef.current)
      setTimeout(() => {
        firedRef.current = false;
      }, 400);
  }, []);

  useEffect(() => cancel, [cancel]);

  const onPointerDown = useCallback(
    (event) => {
      if (!enabled || event.pointerType === 'mouse') return;
      firedRef.current = false;
      startRef.current = { x: event.clientX, y: event.clientY };
      const target = event.currentTarget;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        firedRef.current = true;
        navigator.vibrate?.(10);
        callbackRef.current?.(target);
      }, delay);
    },
    [delay, enabled],
  );

  const onPointerMove = useCallback(
    (event) => {
      const start = startRef.current;
      if (!start || !timerRef.current) return;
      const moved =
        Math.abs(event.clientX - start.x) > MOVE_TOLERANCE_PX ||
        Math.abs(event.clientY - start.y) > MOVE_TOLERANCE_PX;
      if (moved) cancel();
    },
    [cancel],
  );

  const onClickCapture = useCallback((event) => {
    if (!firedRef.current) return;
    firedRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const onContextMenu = useCallback(
    (event) => {
      // Android fires contextmenu on long-press
      if (enabled && (firedRef.current || timerRef.current)) event.preventDefault();
    },
    [enabled],
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onClickCapture,
    onContextMenu,
  };
}
